import React, { useState } from "react";
import {
  Typography,
  IconButton,
  Box,
  Dialog,
  DialogContent,
} from "@mui/material";
import { Line } from "react-chartjs-2";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import CloseIcon from "@mui/icons-material/Close";
import PropTypes from "prop-types";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { chartsPalette } from "config/theme/chartsColorScheme";
import colors from "config/theme/colors";

const customLegendPaddingPlugin = {
  id: "increase-legend-spacing",
  beforeInit(chart) {
    const originalFit = chart.legend.fit;
    chart.legend.fit = function fit() {
      originalFit.bind(chart.legend)();
      this.height += 20;
    };
  },
};

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  customLegendPaddingPlugin
);

const CustomLineChart = ({ title, data, isLegendVisible = true }) => {
  const [isFullscreen, setIsFullscreen] = useState(false);

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const updatedData = {
    ...data,
    datasets: data.datasets.map((dataset, index) => {
      const color = chartsPalette[index];
      return {
        ...dataset,
        backgroundColor: color,
        borderColor: color,
      };
    }),
  };

  return (
    <Box
      sx={{
        position: "relative",
        margin: "0 auto",
        boxShadow: "0 12.01px 32.77px rgba(0, 0, 0, 0.04)",
        padding: 2,
        backgroundColor: colors.white,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography
          variant="h5"
          fontSize={14}
          fontWeight={600}
          sx={{ marginBottom: "20px" }}
        >
          {title}
        </Typography>
        <IconButton onClick={toggleFullscreen}>
          <FullscreenIcon />
        </IconButton>
      </Box>
      <Line
        data={updatedData}
        options={{
          scales: {
            x: {
              time: {
                unit: "month",
              },
            },
            y: {
              title: {
                display: true,
                text: "Ευρώ",
              },
            },
          },
          plugins: {
            tooltip: {
              callbacks: {
                label: function (context) {
                  return context.dataset.label + ": " + context.parsed.y + "€";
                },
              },
            },
            legend: {
              display: isLegendVisible,
              position: "top",
              align: "start",
              labels: {
                usePointStyle: true,
                pointStyle: "rectRounded",
              },
            },
            datalabels: {
              display: false,
            },
          },
        }}
      />
      <Dialog
        open={isFullscreen}
        onClose={toggleFullscreen}
        maxWidth="xl"
        fullWidth
      >
        <DialogContent
          sx={{
            padding: 0,
            height: "80vh",
            maxHeight: "80vh",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
              padding: 2,
            }}
          >
            <Typography variant="h5">{title}</Typography>
            <IconButton onClick={toggleFullscreen}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Box
            sx={{
              width: "100%",
              height: "100%",
              padding: 2,
              boxSizing: "border-box",
            }}
          >
            <Line
              data={updatedData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                  x: {
                    time: {
                      unit: "month",
                    },
                  },
                  y: {
                    title: {
                      display: true,
                      text: "Ευρώ",
                    },
                  },
                },
                plugins: {
                  tooltip: {
                    callbacks: {
                      label: function (context) {
                        return (
                          context.dataset.label + ": " + context.parsed.y + "€"
                        );
                      },
                    },
                  },
                  legend: {
                    display: isLegendVisible,
                    position: "top",
                    align: "start",
                    labels: {
                      usePointStyle: true,
                      pointStyle: "rectRounded",
                    },
                  },
                  datalabels: {
                    display: false,
                  },
                },
              }}
            />
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

CustomLineChart.propTypes = {
  title: PropTypes.string.isRequired,
  data: PropTypes.object.isRequired,
  isLegendVisible: PropTypes.bool,
};

export default CustomLineChart;
